import "angular-ui-mention";
import autosize from "autosize";
import { IUiMention } from "./angular-ui-mention";
import { IAttributes, IAugmentedJQuery, IDirective, IDirectiveFactory, INgModelController, IPromise, IScope } from "angular";
import { IAssignee } from "@gtmhub/assignees";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search, SearchDTO } from "@webapp/search/models/search.models";
import { SearchService } from "../services/search.service";

export const mentionRegex = /(?:\s+|^)@(([\p{L}0-9-_]+)(?: \w+)?)$/gu;

const parseMentioned = (text: string): IAssignee[] => {
  if (!text || text.length === 0) {
    return [];
  }

  const re = /@\[(.*?)\]/g;
  const result = text.match(re);
  if (!result || result.length === 0) {
    return [];
  }

  return result.map((x) => {
    const parts = x.split(":");
    const name = parts[0].substring(2, parts[0].length);
    const email = parts[1];
    const id = parts[2].substring(0, parts[2].length - 1);
    return { id, name, email };
  });
};

export interface IMentionScope extends IScope {
  mentionsChanged(args: { mentions: string[] }): void;
  model: { newText: string };
  $mention: {
    choices: Array<Search<"users"> | Search<"teams">>;
    searching: Array<[string, string | number]>;
    activeChoice: Search<"users"> | Search<"teams">;
  };
}

export class Mention implements IDirective {
  public scope = {
    mentionsChanged: "&mention",
  };
  public require = ["ngModel", "uiMention"];

  constructor(private searchService: SearchService) {}

  public link(scope: IMentionScope, element: IAugmentedJQuery, attrs: IAttributes, ctrls: unknown[]): void {
    const ngModel = ctrls[0] as INgModelController;
    const uiMention = ctrls[1] as IUiMention<IAssignee, IAssignee>;

    // replace angular-ui-mention autogrow functionality with external library
    element.off("input", uiMention.autogrow);
    uiMention.autogrow = () => {
      /* void */
    };
    autosize(element[0]);

    uiMention.encode = (choice) => `${uiMention.delimiter}[${choice.name}:${choice.email}:${choice.id}]`;

    uiMention.label = (choice) => choice.name;

    ngModel.$formatters.push((value) => {
      uiMention.mentions = parseMentioned(value);

      // Manually run render to trigger Jordan's hack on init
      setTimeout(() => {
        uiMention.render(value);
      });

      return value;
    });

    const origRender = uiMention.render;
    uiMention.render = (text?: string) => {
      let mentions: string[] = [];
      // Jordan's hack for auto-resize on the shadow textarea
      if (text) {
        text += "|";
      }

      const result = origRender(text);

      const uiMentions = uiMention.mentions || [];
      const mentionsChanged = mentions.length !== uiMentions.length || uiMentions.some((m) => mentions.indexOf(m.id) < 0);
      if (mentionsChanged) {
        mentions = uiMentions.map((m) => m.id);
        scope.mentionsChanged({ mentions: [...mentions] });
      }

      return result;
    };

    const delimiter = "@";
    uiMention.delimiter = delimiter;
    uiMention.searchPattern = new RegExp(mentionRegex);
    uiMention.decodePattern = new RegExp(`${delimiter}[[sw]+:[0-9a-z-]+]`, "gi");

    uiMention.findChoices = (match: RegExpExecArray, existingMentions: IAssignee[]): IPromise<IAssignee[]> | IAssignee[] => {
      const searchBody: SearchDTO = generateSearchBody(match[1]);
      return this.searchService.getSomeSearchData<Search<Partial<"users">>>(searchBody).then(({ items }) => {
        calculateDropdownSuggestionsPosition();
        return items
          .map(
            ({ id, fields }) =>
              <IAssignee>{
                id,
                email: fields.auth0Cache.email,
                picture: (fields as IAssignee).picture || fields.auth0Cache?.picture,
                name: fields.fullName,
                type: "user",
              }
          )
          .filter((potentialMention) => !existingMentions.some((existingMention) => existingMention.id === potentialMention.id));
      });
    };

    // @NOTE (evgeni.dimov): Override `IUiMention.cancel` since "ui-mention"
    // tracks model updates wrongly and calls the `cancel` method from its
    // `$element.on("keyup click focus", (event) => void)` handler even when there is
    // an active mention pattern match.
    uiMention.cancel = () => {
      const text = (element.val() || "").toString();
      const ele = element[0] as HTMLInputElement | HTMLTextAreaElement;
      const match = uiMention.searchPattern.exec(text.substr(0, ele.selectionStart));

      // If there is a match cancel has been called prematurely and `choices` array
      // needs to be repopulated with correct values.
      if (match) {
        uiMention.search(match);
      } else {
        uiMention.choices = [];
        uiMention.searching = null;
      }
    };

    // Copied and adjusted from angular-ui-mention library
    element.on("keydown", function (event) {
      if (!uiMention.searching) {
        return;
      }

      const isTab = event.keyCode === 9;
      if (!isTab) {
        return;
      }

      uiMention.select();
      uiMention.moved = true;
      event.preventDefault();

      if (!scope.$$phase) {
        scope.$apply();
      }
    });

    function generateSearchBody(searchTerm: string): SearchDTO {
      return {
        searchTerm,
        operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
        searchRequests: [
          {
            collectionName: "users",
            searchFields: [
              {
                name: "firstName",
              },
              {
                name: "lastName",
              },
              {
                name: "fullName",
              },
            ],
          },
        ],
      };
    }

    function calculateDropdownSuggestionsPosition() {
      const textElement: HTMLInputElement = <HTMLInputElement>element[0];
      const text = textElement.value;
      const position = textElement.selectionEnd;
      const area = htmlEncode(text.substring(0, position)) + '<span class="dropdown-tracking"></span>' + htmlEncode(text.substring(position));

      let shadowElement: HTMLElement;

      const isInput = textElement.nodeName.toLowerCase() === "input";

      if (isInput) {
        shadowElement = textElement.parentElement.querySelector(".shadow-input");
      } else {
        shadowElement = textElement.parentElement.querySelector(".shadow-textarea");
      }
      const shadowAreaInnerHtml = shadowElement.innerHTML;
      shadowElement.innerHTML = area;

      const span: HTMLElement = shadowElement.querySelector("span.dropdown-tracking");
      const left = span.offsetLeft + "px";
      const top = span.offsetTop;
      shadowElement.innerHTML = shadowAreaInnerHtml;

      const dropdown: HTMLElement = textElement.parentElement.querySelector("suggestions-dropdown");
      dropdown.style.left = left;

      if (isInput) {
        dropdown.style.top = "100%";
      } else {
        dropdown.style.bottom = "calc(100% - " + top + "px)";
      }
    }

    function htmlEncode(str: string): string {
      const div = document.createElement("div");
      div["textContent" in div ? "textContent" : "innerText"] = str;
      return div.innerHTML;
    }
  }

  public static factory(): IDirectiveFactory {
    const directive = (searchService: SearchService) => new Mention(searchService);
    directive.$inject = ["SearchService"];
    return directive;
  }
}
